import { useEffect, type ReactNode } from 'react'
import { router } from '../router.tsx'
import { useSession } from '../lib/session.ts'
import { LoginScreen } from './LoginScreen.tsx'
import { MfaScreen } from './MfaScreen.tsx'
import { EnrollScreen } from './EnrollScreen.tsx'
import { AcceptExistingScreen, AcceptInviteScreen, ResetPasswordScreen } from './TokenScreens.tsx'
import { PasswordExpiredScreen } from './PasswordExpiredScreen.tsx'

/**
 * Decides which of the four states the app is in, from the server's answer.
 *
 * The gate wraps the router rather than sitting inside it, so there is no
 * authenticated route that can render for a moment before being redirected
 * away — the product simply does not exist until the session does.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { data, isPending, error } = useSession()

  /*
   * Signing in always lands on "/", which sends each role to its own start
   * (Home). Without this the router opened at whatever the address bar held —
   * a reopened tab on a campaign's Overview put the SuperAdmin inside that
   * campaign instead of on the business view (owner, 2026-10-01). Token links
   * keep their address: /accept?signin=1 needs it after the login screens.
   */
  const signingIn =
    data !== undefined && data.state !== 'authenticated' && !new URLSearchParams(window.location.search).get('token')
  useEffect(() => {
    if (signingIn && router.state.location.pathname !== '/') router.history.replace('/')
  }, [signingIn])

  if (isPending) {
    return (
      <div className="grid min-h-screen place-items-center bg-surface">
        <p className="text-sm text-ink-muted">Loading…</p>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="grid min-h-screen place-items-center bg-surface px-4">
        <div className="max-w-sm text-center">
          <p className="text-sm text-ink">Could not reach the server.</p>
          <p className="mt-1 text-xs text-ink-muted">
            The API may be restarting. Reload in a moment.
          </p>
        </div>
      </div>
    )
  }

  /*
   * The two token links, checked before the session state decides anything.
   *
   * They have to win: their holders are anonymous by definition — an invitee
   * has no account, and someone being reset cannot log in — so routing them to
   * the login screen would make the link useless. Read from the URL rather than
   * from the router because the router lives inside this gate, and the whole
   * point of that arrangement is that no product route renders without a
   * session.
   */
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')
  if (token) {
    if (window.location.pathname === '/accept') {
      // Signed in: this is an existing account being added to another
      // campaign (0087). Anonymous: a new account — unless the person has been
      // sent to sign in first (signin=1), in which case the login screens
      // below run with the token still in the address bar and land back here.
      if (data.state === 'authenticated') {
        return <AcceptExistingScreen token={token} email={data.email} />
      }
      if (data.state === 'anonymous' && params.get('signin') !== '1') {
        return <AcceptInviteScreen token={token} />
      }
    }
    if (window.location.pathname === '/reset') return <ResetPasswordScreen token={token} />
  }

  if (data.state === 'anonymous') return <LoginScreen />
  if (data.state === 'mfa_required') {
    return data.enrollment_required ? (
      <EnrollScreen email={data.email} />
    ) : (
      <MfaScreen email={data.email} />
    )
  }

  // Over 365 days (CCR §19012, 0096): the API refuses everything else, so the
  // product is not shown until the password is changed.
  if (data.password_expired) return <PasswordExpiredScreen />

  return <>{children}</>
}
